/**
 *
 * Period
 *
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { get } from 'lodash-es';
import { startOfHour, endOfDay, isFuture, isPast, subMinutes, addMinutes } from 'utils/date-fns/index.js';
import useDialog from 'containers/Dialog/useDialog.js';
import useCurrentRole from 'containers/User/useCurrentRole.js';
import BigCalendar from 'components/interaction/BigCalendar/index.js';
import { FormattedMessage, useIntl } from 'react-intl';
import Icon2 from 'components/media/Icon2/index.js';
import { fixEndForCalendar } from 'utils/eventTimes.js';
import doNothing from 'utils/doNothing.js';
import { idValues } from 'utils/arrays.js';
import isNextYear from 'utils/isNextYear.js';
import cx from 'utils/cx.js';
import BlockModal from './BlockModal/index.jsx';
import blockMessages from './BlockModal/messages.js';
import ActionButtons from '../ActionButtons/index.jsx';
import Toolbar from './Toolbar.jsx';
import messages from './messages.js';
import DayModal from './DayModal.jsx';
import { isInteger } from './helpers.js';

const Wrapper = styled.div`
  .rbc-time-view {
    margin-top: 15px;
  }
  .rbc-time-content {
    height: auto;
  }
  .rbc-time-header > .rbc-row:nth-of-type(2) {
    display: none;
  }
  .rbc-calendar {
    margin-bottom: 15px;
  }
  .slot-disabled {
    cursor: not-allowed;
  }
  .rbc-allday-cell {
    display: none;
  }
  .rbc-timeslot-group {
    min-height: ${(p) => Math.max(p.timeslots + 1, 60)}px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  color: ${(p) => p.theme.gray};
  & > div > span + span {
    padding-left: 0.5rem;
  }
  .bold {
    color: ${(p) => p.theme.textColor};
    font-weight: bold;
  } 
  .shortage {
    color: ${(p) => p.theme.brandDanger};
  }
  .ss-clock {
    color: ${(p) => p.theme.brandPrimary};
    margin-right: 1rem;
  }
`;

function Period(props) {
  const { meetingSet, onNext } = props;
  const { blocks, meetings, timeStep, duration, availableSlots } = meetingSet;
  const role = useCurrentRole();
  const yearEnd = new Date(get(role, 'school.schoolYear.schoolYearEnd'));
  const [openBlock, setOpenBlock] = useState(null);
  const [openDay, setOpenDay] = useState(null);
  const intl = useIntl();
  const { dlgAlert } = useDialog();
  const meetingsCount = meetings.list.length;
  const events = idValues(blocks).map((o, idx) => ({ ...fixEndForCalendar(o), pos: idx + 1 }));
  const slotsPerHour = 60 / (timeStep);

  function findEvent(time) {
    return events.find((event) => event.start < time && event.end > time);
  }

  function fitSlotsInHour(info) {
    if (isInteger(slotsPerHour) && slotsPerHour >= 4) {
      // Whole number per hour, no need to recalculate slots
      return info;
    }
    const slots = [];
    let { start } = info;
    // Last slot should end before end time, so start before end - duration
    const end = subMinutes(info.end, duration);
    do {
      slots.push(start);
      start = addMinutes(start, timeStep);
    } while (start <= end);
    return { ...info, slots, end: start };
  }

  function selectSlot(info) {
    const { action, start, slots } = info;
    if (action === 'doubleClick') {
      // handled by first click
      return;
    }
    const selectedEvent = action === 'select' && slots.length < 3 && findEvent(start);
    if (selectedEvent) {
      // Was dragging instead of clicking on an event
      selectEvent(selectedEvent);
      return;
    }
    if (isPast(start)) {
      dlgAlert({ message: messages.futureOnly });
      return;
    }
    if (isNextYear(start, yearEnd)) {
      dlgAlert({ message: messages.noNextYear });
      return;
    }
    if (action === 'click') {
      setOpenDay(start);
      return;
    }
    selectEvent(fitSlotsInHour(info));
  }

  function selectEvent(info) {
    setOpenBlock((prevBlock) => prevBlock ? {} : info);
  }

  function titleAccessor(info) {
    const values = {
      count: info.availableSlots,
      open: info.openSlots,
    };
    return intl.formatMessage(blockMessages.slotCount, values);
  }

  function slotPropGetter(time) {
    return isFuture(time) ? {} : { className: 'slot-disabled' };
  }

  const toolbarWithBlocks = (p) => <Toolbar {...p} blocks={events} />;

  function dateClick(date) {
    if (isFuture(endOfDay(date))) {
      const day = new Date(date);
      day.setHours(new Date().getHours() + 1);
      setOpenDay(startOfHour(day));
    } else {
      dlgAlert({ message: messages.futureOnly });
    }
  }

  const min = new Date(new Date().setHours(8, 0, 0));
  // For allocation, move to first event
  // Should be whole number of selectable slots per hour, if less than 3, make 4 (every 15 minutes)
  const timeslotsPerHour = isInteger(slotsPerHour) && slotsPerHour >= 4 ? slotsPerHour : 4;

  const drilldownProperties = { onDrillDown: dateClick };
  return (
    <Wrapper timeslots={timeslotsPerHour}>
      <Header>
        <div>
          <span className="bold"><FormattedMessage {...messages.timeslots} /></span>
          <span>-</span>
          <span className="grow">
            <FormattedMessage {...messages.helptext} />
          </span>
        </div>
        <div>
          <Icon2 className="ss-clock py-0 fs-4 lower-1" />
          <span className={cx({ 'text-danger': availableSlots < meetingsCount })}>
            {availableSlots}
            /
            {meetingsCount}
          </span>
          <FormattedMessage {...messages.requiredSlots} />
        </div>
      </Header>
      <BigCalendar
        selectable
        events={events}
        min={min}
        view="week"
        onSelectSlot={selectSlot}
        onSelectEvent={selectEvent}
        titleAccessor={titleAccessor}
        tooltipAccessor={titleAccessor}
        slotPropGetter={slotPropGetter}
        onView={doNothing}
        step={60 / timeslotsPerHour}
        timeslots={timeslotsPerHour}
        components={{ toolbar: toolbarWithBlocks }}
        {...drilldownProperties}
      />
      <div className="mb-4">
        <ActionButtons onSubmit={onNext} />
      </div>
      { openDay && <DayModal value={openDay} onHide={() => setOpenDay(null)} onChange={selectSlot} slotLength={timeStep} />}
      { openBlock && (
        <BlockModal {...props} block={openBlock} onHide={() => setOpenBlock(null)} />
      )}
    </Wrapper>
  );
}

Period.propTypes = {
  meetingSet: PropTypes.object,
  onNext: PropTypes.func,
};

export default Period;
